import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
if (!BACKEND_URL || BACKEND_URL === "undefined") {
    // Fail loudly at startup — better than silently posting to /undefined/api/...
    // eslint-disable-next-line no-console
    console.error(
        "[FATAL] REACT_APP_BACKEND_URL is not set. " +
        "Create frontend/.env (or .env.production) with REACT_APP_BACKEND_URL=https://your-domain.com BEFORE running `yarn build`."
    );
    throw new Error("REACT_APP_BACKEND_URL is not set — see console for fix.");
}
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
    baseURL: API,
    withCredentials: true,
});

// Defensive guard: reject any response whose body isn't actually JSON.
// This is the classic signature of an nginx misconfig where /api/* falls
// through to the SPA `index.html` (HTML string with 200 OK) — without this
// interceptor every consumer would crash on `.filter`/`.map`/`.length`.
api.interceptors.response.use(
    (resp) => {
        const ct = (resp.headers?.["content-type"] || "").toLowerCase();
        if (typeof resp.data === "string" && ct.includes("text/html")) {
            const err = new Error(
                "Backend returned HTML instead of JSON — check that nginx routes /api/* to the API server, not the SPA."
            );
            err.response = resp;
            return Promise.reject(err);
        }
        return resp;
    },
    (err) => Promise.reject(err)
);

export function fmtError(detail) {
    if (detail == null) return "Something went wrong.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export const ROLE_LABELS = {
    super_admin: "Super Admin",
    client_servicing: "Client Servicing",
    execution: "Execution",
    execution_head: "Execution Head",
    delivery: "Delivery",
    delivery_head: "Delivery Head",
    reporting: "Reporting",
    accounts: "Accounts",
    client_user: "Client",
};

export const STAGE_STYLES = {
    intake: { bg: "#F1F5F9", text: "#475569", border: "#CBD5E1" },
    execution: { bg: "#FEF3C7", text: "#B45309", border: "#FCD34D" },
    delivery: { bg: "#E0E7FF", text: "#4338CA", border: "#A5B4FC" },
    live: { bg: "#DCFCE7", text: "#15803D", border: "#86EFAC" },
    reporting: { bg: "#F3E8FF", text: "#7E22CE", border: "#D8B4FE" },
    closed: { bg: "#F3F4F6", text: "#6B7280", border: "#E5E7EB" },
};

export const STAGE_ORDER = ["intake", "execution", "delivery", "live", "reporting", "closed"];

export const BILLING_LABELS = {
    pending: "Pending",
    ready_for_invoice: "Ready for Invoice",
    invoiced: "Invoiced",
    paid: "Paid",
};

import { getCurrencyMeta } from "@/lib/currencies";

export function fmtMoney(n, currency = "USD") {
    const v = Number(n || 0);
    const meta = getCurrencyMeta(currency);
    const symbol = meta?.symbol || currency || "$";
    // Use a tiny separator only when the symbol is a multi-char prefix (e.g. AED, FCFA, etc.)
    const sep = symbol.length > 1 && !"$£€¥₹₽₩₺₪₫₸₴₦₱฿₡₲₣₵".includes(symbol) ? " " : "";
    return symbol + sep + v.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

export function fmtNumber(n) {
    return Number(n || 0).toLocaleString();
}

export function fmtDate(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
}

export function fmtDateTime(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
    });
}
